"use client";

import { type ReactNode, useEffect, useReducer, useState } from "react";
import { LoaderPendingContext, type LoaderResult } from "./LoaderResult";

/**
 * The data of a `raceLoaderData` result, or undefined while it is still loading. A request that
 * fails later is thrown while rendering, so it reaches the route's error (or not-found) boundary like
 * a loader error. Once some data has been shown, a newer pending result (`router.invalidate()`)
 * keeps showing it until the new data arrives.
 */
const useLoaderResult = <T,>(result: LoaderResult<T>): { data?: T; pending: boolean } => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const [shown, setShown] = useState<{ data: T }>();
  useEffect(() => {
    if (result.status !== "pending") return;
    let active = true;
    void result.settled?.then(() => {
      if (active) rerender();
    });
    return () => {
      active = false;
    };
  }, [result]);
  // Remember the data shown last, for a later pending result of the same page.
  const readyData = result.status === "ready" ? result.data : undefined;
  useEffect(() => {
    if (result.status === "ready") setShown({ data: readyData as T });
  }, [result, readyData]);
  if (result.status === "error") throw result.error;
  if (result.status === "ready") return { data: result.data, pending: false };
  return { data: shown?.data, pending: !shown };
};

export interface DeferredLoaderDataProps<T> {
  result: LoaderResult<T>;
  /** Renders the real page; `data` is undefined while it is loading. */
  children: (data: T | undefined) => ReactNode;
}

/**
 * Renders a page from a `raceLoaderData` result: with the data, or, while it is loading, the same
 * page with `useLoaderPending()` set, which then receives the data in place once it arrives. The page
 * stays mounted, so what someone did meanwhile (a dialog opened, a field typed into) is kept;
 * components that start from the data take it when it arrives (`useSeededAsyncState`).
 */
export function DeferredLoaderData<T>(props: DeferredLoaderDataProps<T>) {
  const { data, pending } = useLoaderResult(props.result);
  return (
    <LoaderPendingContext.Provider value={pending}>
      {props.children(data)}
    </LoaderPendingContext.Provider>
  );
}
